import React from 'react';
import {Box, Typography} from '@mui/material';
import {BuilderPot, BuilderPot_dentistChildren} from '../../../schema-types/BuilderPot';
import {ParentPlanRow} from '../CreateBuilder/BuilderSourceDestination/ParentPlanRow';
import {PlanAccordionShell} from '../CreateBuilder/BuilderSourceDestination/PlanAccordionShell';
import {PlanSelector} from '../CreateBuilder/BuilderSourceDestination/PlanSelector';
import {PlanValidationIcon} from '../CreateBuilder/BuilderSourceDestination/PlanValidationIcon';
import {usePlanValidationLevel} from '../CreateBuilder/BuilderSourceDestination/usePlanValidationLevel';
import {
  getSummary,
  getSummaryParts,
} from '../CreateBuilder/BuilderSourceDestination/builderNaming';
import {getCurrencyFloat, getCurrencyString} from '../../../utils/currencyFormatter';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {useAutoForm} from '../../../components/AutoForm/useAutoForm';
import {TransferDestinationAmounts} from './TransferDestinationAmounts';
import {TransferDestinationSubplan} from './TransferDestinationSubplan';
import {getTotalTransferAmount} from '../hooks/builderTransferHooks';

const TransferDestExtraText: React.FC<{
  pot: BuilderPot | BuilderPot_dentistChildren;
  visible: boolean;
}> = ({pot, visible}) => {
  const {values} = useAutoForm<builderFieldsType>();
  if (!visible) return null;
  const totalTransferAmount = getTotalTransferAmount(
    Object.values(values.transfer?.transferSources || {})
  );
  return (
    <Typography>
      <strong data-testid="extra-text-id">{` - Post Contribution: ${getCurrencyString(
        getCurrencyFloat(pot.totalValue) + totalTransferAmount
      )}`}</strong>
    </Typography>
  );
};

interface TransferDestinationParentProps {
  pot: BuilderPot;
  destinationExistingPotId: string | null;
  disabled?: boolean;
  fixed?: boolean;
  sameReview?: boolean;
  handleChange: (pot: BuilderPot | BuilderPot_dentistChildren) => void;
}

export const TransferDestinationParent: React.FC<TransferDestinationParentProps> = ({
  pot,
  destinationExistingPotId,
  disabled,
  fixed,
  sameReview,
  handleChange,
}) => {
  const {values} = useAutoForm<builderFieldsType>();
  const dentistChildren: BuilderPot_dentistChildren[] = pot.dentistChildren ?? [];
  const hasChildren = dentistChildren.length > 0;
  const isLeafSelected = !hasChildren && destinationExistingPotId === pot.id;
  const childSelected =
    hasChildren && dentistChildren.some((c) => c.id === destinationExistingPotId);
  const activeFormikKey =
    isLeafSelected || childSelected ? 'transfer.destinationValues' : undefined;
  const {bgClass} = usePlanValidationLevel(activeFormikKey);

  if (hasChildren) {
    const {label, currentValue} = getSummaryParts(pot);
    return (
      <ParentPlanRow
        pot={pot}
        isSelected={childSelected}
        sameReview={sameReview}
        validationLevel={bgClass}
        summaryChildren={
          <Box sx={{display: 'flex', alignItems: 'center', flex: 1}}>
            <Typography variant="body1" sx={{fontWeight: 600}}>
              {label}
            </Typography>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-end"
              sx={{ml: 'auto'}}
            >
              {currentValue && (
                <Typography variant="body2" sx={{fontWeight: 600}}>
                  {currentValue}
                </Typography>
              )}
              <TransferDestExtraText pot={pot} visible={childSelected} />
            </Box>
          </Box>
        }
        summaryTrailing={
          childSelected && activeFormikKey ? (
            <PlanValidationIcon formikKeyPrefix={activeFormikKey} />
          ) : undefined
        }
      >
        {dentistChildren.map((child) => (
          <TransferDestinationSubplan
            key={child.id}
            subPlan={child}
            isSelected={destinationExistingPotId === child.id}
            disabled={disabled}
            handleChange={() => handleChange(child)}
          />
        ))}
      </ParentPlanRow>
    );
  }

  return (
    <ParentPlanRow pot={pot} isSelected={isLeafSelected} sameReview={sameReview}>
      <PlanAccordionShell
        expanded={isLeafSelected}
        isSelected={isLeafSelected}
        isSoleParent
        validationLevel={bgClass}
        sameReview={sameReview}
        summaryChildren={
          <>
            <PlanSelector
              mode="single"
              id={pot.id}
              isSelected={isLeafSelected}
              fixed={fixed}
              handleChange={() => handleChange(pot)}
            />
            <Box display="flex" flexDirection="row">
              {getSummary(pot)}
              &nbsp;
              <TransferDestExtraText pot={pot} visible={isLeafSelected} />
            </Box>
          </>
        }
        summaryTrailing={
          activeFormikKey ? (
            <PlanValidationIcon formikKeyPrefix={activeFormikKey} />
          ) : undefined
        }
      >
        {isLeafSelected && (
          <TransferDestinationAmounts
            pot={pot}
            disabled={disabled ?? false}
            mainPlanType={values.mainPlanType}
          />
        )}
      </PlanAccordionShell>
    </ParentPlanRow>
  );
};
